/**
 * CreateBranchModal - Modal for creating a new branch.
 */

import { useState } from "react";
import { useGitStore } from "@/stores/gitStore";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, GitBranch, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Branch {
  name: string;
  sha: string;
  protected: boolean;
}

interface CreateBranchModalProps {
  owner: string;
  repo: string;
  branches: Branch[];
}

export function CreateBranchModal({
  owner,
  repo,
  branches,
}: CreateBranchModalProps) {
  const { createBranch, isSaving } = useGitStore();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [fromBranch, setFromBranch] = useState(branches[0]?.name || "main");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError("Branch name is required");
      return;
    }

    // Basic branch name validation
    const invalidChars = /[~^:?*\[\]\\@{}\s]/;
    if (invalidChars.test(name)) {
      setError("Branch name contains invalid characters");
      return;
    }

    const result = await createBranch(
      owner,
      repo,
      name.trim(),
      fromBranch
    );

    if (result) {
      toast({
        title: "Branch created",
        description: `Branch "${name}" has been created successfully.`,
      });
      setOpen(false);
      setName("");
    } else {
      const storeError = useGitStore.getState().error;
      setError(storeError?.message || "Failed to create branch");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Create Branch
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="w-5 h-5" />
              Create Branch
            </DialogTitle>
            <DialogDescription>
              Create a new branch from an existing branch
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="branch-name">Branch name</Label>
              <Input
                id="branch-name"
                placeholder="feature/my-feature"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSaving}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="from-branch">Create from</Label>
              <Select value={fromBranch} onValueChange={setFromBranch} disabled={isSaving}>
                <SelectTrigger id="from-branch">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.name} value={branch.name}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || !name.trim()}>
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Branch
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
